import { Dispatch, Fragment, useCallback, useMemo, type FC } from 'react';

// MUI
import Box from '@mui/material/Box';

// screen
import { WEEKDAY_NUMBERS, FIELD_WIDTHS } from 'modules/map/components/daily-dish/constants';
import {
  getLayoutConfig,
  WEEKDAY_TITLE_STYLE_OPTIONS,
  WEEKDAY_TITLE_POSITION_OPTIONS,
  MENU_LIMITS,
} from 'modules/map/components/daily-dish/config';

import WeekdayIconSelector from 'modules/map/components/daily-dish/components/utils/WeekdayIconSelector';
import {
  NumberFieldWithLimits,
  SelectField,
  ColorPickerField,
  SwitchField,
  FormFieldRow,
} from 'modules/map/components/daily-dish/components/form';
import { SettingsAction } from 'modules/map/components/daily-dish/reducer';
import { TemplateSettings } from 'modules/map/components/daily-dish/types';

interface DailyMenuTemplateSettingsPanelProps {
  settings: TemplateSettings;
  dispatch: Dispatch<SettingsAction>;
}

const DailyMenuTemplateSettingsPanel: FC<DailyMenuTemplateSettingsPanelProps> = (props) => {
  const { settings, dispatch } = props;

  const weekdayColors = useMemo(
    () =>
      WEEKDAY_NUMBERS.map((num) => ({
        weekday: num as 2 | 3 | 4 | 5 | 6,
        label: `Màu viền Thứ ${num}`,
        displayLabel: `Thứ ${num}`,
      })),
    []
  );

  const handleChangeColor = useCallback(
    (weekday, color: string) => {
      dispatch({ type: 'UPDATE_WEEKDAY_COLOR', weekday, color });
    },
    [dispatch]
  );

  const onColorChanges = useMemo(() => {
    return WEEKDAY_NUMBERS.map(
      (num) => (color: string) => handleChangeColor(num as 2 | 3 | 4 | 5 | 6, color)
    );
  }, [handleChangeColor]);

  return (
    <FormFieldRow>
      <SelectField
        label="Kiểu tiêu đề ngày"
        value={settings.menu.weekdayTitleStyle}
        options={WEEKDAY_TITLE_STYLE_OPTIONS}
        disabled={!getLayoutConfig(settings.general.layoutId)?.capabilities.canChangeTitleStyle}
        onChange={(value) => {
          const parsed = parseInt(value.toString());
          if (!Number.isNaN(parsed)) {
            dispatch({
              type: 'UPDATE_MENU_STYLE',
              field: 'weekdayTitleStyle',
              value: parsed,
            });
          }
        }}
      />

      <SelectField
        label="Vị trí tiêu đề ngày"
        value={settings.menu.weekdayTitlePosition}
        options={WEEKDAY_TITLE_POSITION_OPTIONS}
        onChange={(value) => {
          dispatch({
            type: 'UPDATE_MENU_STYLE',
            field: 'weekdayTitlePosition',
            value: value as 'top' | 'bottom',
          });
        }}
      />

      <NumberFieldWithLimits
        label="Độ dày viền của khung"
        value={settings.menu.borderWidth}
        min={MENU_LIMITS.borderWidth.min}
        max={MENU_LIMITS.borderWidth.max}
        onChange={(value) => {
          dispatch({
            type: 'UPDATE_MENU_STYLE',
            field: 'borderWidth',
            value,
          });
        }}
      />

      <NumberFieldWithLimits
        label="Độ bo góc"
        value={settings.menu.borderRadius}
        min={MENU_LIMITS.borderRadius.min}
        max={MENU_LIMITS.borderRadius.max}
        onChange={(value) => {
          dispatch({
            type: 'UPDATE_MENU_STYLE',
            field: 'borderRadius',
            value,
          });
        }}
      />

      <SwitchField
        label="Emoji"
        checked={settings.menu.emojiEnabled}
        onChange={(checked) => {
          dispatch({
            type: 'UPDATE_MENU_STYLE',
            field: 'emojiEnabled',
            value: checked,
          });
        }}
      />

      {/* Empty box to maintain grid layout */}
      <Box sx={{ width: FIELD_WIDTHS.HALF }} />

      {weekdayColors.map((item, index) => (
        <Fragment key={item.weekday}>
          <ColorPickerField
            label={item.label}
            color={settings.menu.weekday[item.weekday].borderColor}
            onColorChange={onColorChanges[index]}
          />
          <Box sx={{ width: FIELD_WIDTHS.HALF }}>
            <WeekdayIconSelector
              weekDay={item.displayLabel}
              backgroundColor={settings.menu.weekday[item.weekday].borderColor}
              weekdayNumber={item.weekday}
              selectedEmojiId={settings.menu.weekday[item.weekday].emojiId}
              dispatch={dispatch}
            />
          </Box>
        </Fragment>
      ))}
    </FormFieldRow>
  );
};

export default DailyMenuTemplateSettingsPanel;
